import { StylishNewButton } from '../DesignSystems/New/StylishNewButton';
import { StylishNewSearchBar } from '../DesignSystems/New/StylishNewSearchBar';
import { StylishNewToggleButtonGroup } from '../DesignSystems/New/StylishNewToggleButtonGroup';
import SimpleEntityList from '../DesignSystems/New/SimpleEntityList';
import React, { useMemo, useState } from 'react';
import { setCurrentSitrepId, setSelectedDChat } from '../../slices/dchatSlice';
import { useDispatch, useSelector } from 'react-redux';
import {
  useArchiveDisasterChat,
  useDisasterChats,
  useRestoreDisasterChat,
} from './hooks/useDisasterChats';
import DisasterChatEditDrawer from './DisasterChatEditDrawer';
import DeleteModal from '../DesignSystems/DeleteModal';
import { ListGroup, ListGroupItem } from 'react-bootstrap';
import { SharedIcon } from '../SharedIcon/SharedIcon';
import ChatDisasterGPT from './ChatDisasterGPT';
import ChatScopeSelector, {
  ChatScope,
  SELF_SCOPE,
  chatOwnerLabel,
} from './ChatScopeSelector';
import { selectUserGuid, useAppSelector } from '../../slices/commonSelectors';
import type {
  ChatSurface,
  DisasterChatSummary,
  SelectedDisasterChat,
} from './chatTypes';
import { BsMicrosoftTeams } from 'react-icons/bs';

type DisasterChatManagerProps = {
  selectedDatetime?: string;
  currentView?: 'chat' | 'list';
  setCurrentView?: (view: 'chat' | 'list') => void;
};

type ChatListView = 'active' | 'archived';

const LIST_VIEW_OPTIONS: { value: ChatListView; label: string }[] = [
  { value: 'active', label: 'Active' },
  { value: 'archived', label: 'Archived' },
];

// None of the installed icon packs has the Microsoft 365 Copilot glyph.
const CopilotIcon = ({ title }: { title: string }) => (
  <svg
    className="me-2 flex-shrink-0"
    width="1em"
    height="1em"
    viewBox="-1 -1 26 26"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    role="img"
    aria-label={title}
  >
    <title>{title}</title>
    {/* Dash gaps (percent of each outline) break the line where it passes under the other one. */}
    <g transform="rotate(12 12 12)">
      <rect
        x="3"
        y="2.5"
        width="10"
        height="14"
        rx="4.5"
        pathLength="100"
        strokeDasharray="18 10 72"
      />
      <rect
        x="11"
        y="7.5"
        width="10"
        height="14"
        rx="4.5"
        pathLength="100"
        strokeDasharray="68 10 22"
      />
    </g>
  </svg>
);

const ChannelSurfaceBadge = ({ surface }: { surface?: ChatSurface }) => {
  if (surface === 'teams') {
    return (
      <BsMicrosoftTeams
        className="me-2 flex-shrink-0"
        color="#6264A7"
        title="Started in Microsoft Teams"
      />
    );
  }
  if (surface === 'm365_copilot') {
    return <CopilotIcon title="Started in Microsoft 365 Copilot" />;
  }
  return null;
};

const DisasterChatManager = ({
  selectedDatetime,
  currentView: propCurrentView = 'chat',
  setCurrentView: propSetCurrentView,
}: DisasterChatManagerProps) => {
  const dispatch = useDispatch();
  const myGuid = useAppSelector(selectUserGuid);

  // Whose chats the list shows (PNG-500). Chats owned by others open read-only;
  // the selector itself only renders for users holding incident:viewSharedChats.
  const [chatScope, setChatScope] = useState<ChatScope>(SELF_SCOPE);
  // Active vs archived — only meaningful for the caller's own chats (PNG-537).
  const [listView, setListView] = useState<ChatListView>('active');
  const isSelfScope = chatScope.scope === 'self';
  const showArchived = isSelfScope && listView === 'archived';
  const showOwner = !isSelfScope;

  const { data, isLoading } = useDisasterChats({
    scope: chatScope.scope,
    ownerGuid: chatScope.scope === 'user' ? chatScope.ownerGuid : null,
    archived: showArchived,
  });
  const chats = useMemo(() => (data ?? []) as DisasterChatSummary[], [data]);
  const archiveDisasterChat = useArchiveDisasterChat();
  const restoreDisasterChat = useRestoreDisasterChat();

  // Get current selected chat from Redux
  const selectedChat = useSelector(
    (state: { dchat: { dchat: SelectedDisasterChat } }) => state.dchat.dchat
  );

  const [chatToEdit, setChatToEdit] = useState<DisasterChatSummary | null>(
    null
  );
  const [chatToArchive, setChatToArchive] = useState<
    DisasterChatSummary | undefined
  >();
  const [filterText, setFilterText] = useState('');
  // Use local state as fallback when props are not provided
  const [localCurrentView, setLocalCurrentView] = useState<'chat' | 'list'>(
    'chat'
  );

  const currentView = propSetCurrentView ? propCurrentView : localCurrentView;
  const setCurrentView = propSetCurrentView || setLocalCurrentView;

  const handleChatClick = (chat: SelectedDisasterChat) => {
    dispatch(setCurrentSitrepId(undefined));
    dispatch(setSelectedDChat(chat));
    setCurrentView('chat'); // Switch to chat view
  };

  const handleAddChat = () => {
    // "Add" means start a new chat (empty chat object)
    handleChatClick({});
  };

  const handleToggleView = () => {
    setCurrentView(currentView === 'chat' ? 'list' : 'chat');
  };

  const handleChatClose = () => {
    // Close chat and return to list view
    dispatch(setSelectedDChat({}));
    dispatch(setCurrentSitrepId(undefined));
    setCurrentView('list');
  };

  const handleArchiveChat = (chat: DisasterChatSummary) => {
    setChatToArchive(chat);
  };

  const handleConfirmArchive = () => {
    if (chatToArchive) {
      archiveDisasterChat.mutate(chatToArchive.id, {
        onSettled: () => {
          setChatToArchive(undefined);
        },
      });
    }
  };

  const handleRestoreChat = (chat: DisasterChatSummary) => {
    restoreDisasterChat.mutate(chat.id);
  };

  const handleEditChat = (chat: DisasterChatSummary) => {
    setChatToEdit(chat);
  };

  const handleScopeChange = (scope: ChatScope) => {
    setChatScope(scope);
    // Leaving "My chats" always lands on the live list (the Archived view is owner-only).
    if (scope.scope !== 'self') setListView('active');
  };

  const isOwnChat = (chat: DisasterChatSummary) =>
    !!myGuid && chat.user_guid === myGuid;

  const filteredChats = useMemo(() => {
    const lowerCaseFilter = filterText.toLowerCase();
    return chats.filter((chat) => {
      if (typeof chat.name !== 'string') {
        console.warn('Encountered chat with invalid name:', chat);
        return false;
      }
      // Match the chat name or its owner so "who worked on what" is searchable
      // in the shared scopes.
      return [chat.name, chat.owner_name, chat.owner_email].some(
        (field) =>
          typeof field === 'string' &&
          field.toLowerCase().includes(lowerCaseFilter)
      );
    });
  }, [chats, filterText]);

  return (
    <>
      {currentView === 'chat' ? (
        <div className="d-flex flex-column h-100">
          {/* Chat Header with List Button */}
          <div
            style={{
              display: 'flex',
              gap: '8px',
              margin: '8px',
              alignItems: 'center',
              borderBottom: '1px solid #dee2e6',
              paddingBottom: '8px',
            }}
          >
            <StylishNewButton
              className="button--secondary button--icon"
              onClick={handleToggleView}
              title="View chat list"
            >
              <SharedIcon iconName={'list'} size={'sm'} />
            </StylishNewButton>
            <h5 className="m-0" style={{ flexGrow: 1 }}>
              {selectedChat?.name || '(New chat)'}
            </h5>
          </div>

          {/* Chat Interface */}
          <div style={{ flexGrow: 1, overflow: 'hidden' }}>
            <ChatDisasterGPT
              selectedDatetime={selectedDatetime}
              toggle={handleChatClose}
            />
          </div>
        </div>
      ) : (
        <div className="d-flex flex-column h-100">
          {/* List Header: scope selector (permission-gated) on its own row, then actions */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              margin: '8px',
            }}
          >
            <ChatScopeSelector value={chatScope} onChange={handleScopeChange} />
            <div
              style={{
                display: 'flex',
                gap: '8px',
                alignItems: 'center',
              }}
            >
              <StylishNewButton
                onClick={handleAddChat}
                className="button--primary"
              >
                Add
              </StylishNewButton>
              <div style={{ flexGrow: 1 }}>
                <StylishNewSearchBar
                  value={filterText}
                  onChangeSearchTerm={(val) => setFilterText(val)}
                  placeholder={
                    showOwner
                      ? 'Filter by name or user'
                      : 'Filter chats by name'
                  }
                />
              </div>
            </div>
            {isSelfScope && (
              <StylishNewToggleButtonGroup
                size="sm"
                data={LIST_VIEW_OPTIONS}
                selected={listView}
                onChange={(value) => setListView(value as ChatListView)}
              />
            )}
          </div>

          {/* Chat List */}
          <div style={{ flexGrow: 1, overflowY: 'auto' }}>
            {isLoading ? (
              <div style={{ textAlign: 'center', marginTop: '20px' }}>
                <span>Loading...</span>
              </div>
            ) : filteredChats.length > 0 ? (
              <ListGroup className={'py-0'}>
                <SimpleEntityList<DisasterChatSummary[]>
                  entities={filteredChats}
                  renderEntity={(chat) => (
                    <ListGroupItem
                      role="button"
                      tabIndex={0}
                      key={chat.id}
                      className="d-flex justify-content-between align-items-center py-3"
                      onClick={() => handleChatClick(chat)}
                      onKeyDown={(e) => {
                        // Prevent double-triggering when user presses Enter/Space on nested buttons
                        if (e.target instanceof HTMLButtonElement) {
                          return;
                        }
                        if (e.key === 'Enter' || e.key === ' ') {
                          handleChatClick(chat);
                        }
                      }}
                    >
                      <div>
                        <div className="d-flex align-items-center">
                          <ChannelSurfaceBadge surface={chat.surface} />
                          <strong title={chat.name}>
                            {chat.name.length > 50
                              ? `${chat.name.slice(0, 50)}...`
                              : chat.name}
                          </strong>
                        </div>
                        {showOwner && (
                          <div className="small text-muted">
                            {chatOwnerLabel(chat)}
                          </div>
                        )}
                      </div>
                      {/* Actions stay owner-only: others' chats are view-only. Archived rows only ever come from the
                          self scope, but the owner gate wraps both branches so that stays true if that changes. */}
                      {!isOwnChat(chat) ? null : showArchived ? (
                        <div>
                          <StylishNewButton
                            className="button--secondary button--icon"
                            title="Restore chat"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRestoreChat(chat);
                            }}
                          >
                            <SharedIcon iconName={'unarchive'} size={'sm'} />
                          </StylishNewButton>
                        </div>
                      ) : (
                        <div>
                          <StylishNewButton
                            className="button--secondary button--icon me-2"
                            title="Rename chat"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditChat(chat);
                            }}
                          >
                            <SharedIcon iconName={'edit'} size={'sm'} />
                          </StylishNewButton>
                          <StylishNewButton
                            className="button--secondary button--icon"
                            title="Archive chat"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleArchiveChat(chat);
                            }}
                          >
                            <SharedIcon iconName={'archive'} size={'sm'} />
                          </StylishNewButton>
                        </div>
                      )}
                    </ListGroupItem>
                  )}
                />
              </ListGroup>
            ) : (
              <div style={{ textAlign: 'center', marginTop: '20px' }}>
                <span>
                  {showArchived ? 'No archived chats.' : 'No chats found.'}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {chatToEdit && (
        <DisasterChatEditDrawer
          show={!!chatToEdit}
          onHide={() => setChatToEdit(null)}
          chat={chatToEdit}
        />
      )}
      {chatToArchive ? (
        <DeleteModal
          title="Archive Session"
          confirmLabel="Archive"
          onClose={() => setChatToArchive(undefined)}
          onConfirmDelete={handleConfirmArchive}
        >
          Archive this PRATUS AI Agent session? It will be hidden from your chat
          list; you can restore it from Archived.
        </DeleteModal>
      ) : null}
    </>
  );
};

export default DisasterChatManager;
